import { SelectResponse } from 'merchant-api/types';
import { PricingResponse } from '../getPricingDetails/getPricingDetails';
import { pricingToOfferSelect } from './helper';
import useDetailedOffers, {
} from '../useDetailedOffers/useDetailedOffers';
import {
  mutationOptions,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { offerOrchestrator } from '..';
import { voidCheckouts } from 'merchant-api/checkout';
import { AppError, queryClient } from 'merchant-api';
import { useApplication } from 'merchant-api/unified';
import { AxiosError } from 'axios';

export interface OfferSelectRequest {
  apr: number;
  centsOnDollar: number;
  disbursementAmount: number;
  installmentAmount: number;
  lender: string;
  lineAmount: number;
  numberOfPayments: number;
  ofeeAmount: number;
  paymentFrequency: string;
  totalDrawCost: number;
  totalPaybackAmount: number;
  unifiedPricingModelId: string;
}

export interface SelectOfferProps {
  response: any;
  selecting: boolean;
  selectionError: Error | null;
  select: (args: PricingResponse) => Promise<SelectResponse>;
  mutation: UseMutationResult<SelectResponse, Error, PricingResponse>;
}

interface SelectOfferRequest {
  offerId: string;
  offerSelectRequest: OfferSelectRequest;
  applicationUuid: string;
}

const selectingOffer = async (data: SelectOfferRequest) => {
  if (!data.applicationUuid) throw AppError.OfferSelectFailed;
  try {
    // Voiding first means a failure here leaves the merchant on their original pricing with a usable
    // checkout, rather than a contract for pricing they replaced.
    await voidCheckouts(data.applicationUuid);
  } catch (error) {
    // Only the void is wrapped: a failing select keeps its AxiosError so ErrorPage can still read
    // response.data.message (the tax-id sales alert) and the analytics event keeps status and body.
    if (error instanceof AppError) throw error;
    throw AppError.OfferSelectFailed;
  }

  const selected = await offerOrchestrator
    .post<SelectResponse>(
      `/v2/offers/${data.offerId}/select`,
      data.offerSelectRequest,
    )
    .then((res) => res.data);

  await queryClient.invalidateQueries({ queryKey: ['checkout'] });

  return selected;
};

export function selectOffer(offerId?: string): SelectOfferProps {
  const queryClient = useQueryClient();
  const { response } = useDetailedOffers();
  const { applicationData } = useApplication(['APPROVED']);

  const mutation = useMutation({
    mutationFn: async (arg: PricingResponse) => {
      const data = await selectingOffer({
        offerId: offerId!,
        offerSelectRequest: pricingToOfferSelect(arg),
        applicationUuid: applicationData?.id ?? '',
      });
      await queryClient.setQueryData(['detailedOffers', applicationData?.id], {
        ...response,
        applicationOffer: {
          ...response.applicationOffer,
          selectedOffer: data?.selectedOffer,
        },
      });
      return data;
    },
    mutationKey: ['selectOffer', offerId],
  });

  return {
    response: mutation.data,
    selecting: mutation.isPending,
    selectionError: mutation.error,
    select: mutation.mutateAsync,
    mutation,
  };
}

export const selectOfferOpts = () =>
  mutationOptions<SelectResponse, AxiosError, SelectOfferRequest>({
    mutationKey: ['selectOffer'],
    mutationFn: selectingOffer,
  });

export default selectOffer;
